import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import Modal from '@components/ui/Modal.tsx';
import { formatAmountPlain, formatDate, formatDateTime, formatMoney } from '@utils/formats.ts';
import { useAdminRequisitionStore } from './useAdminRequisitionStore.ts';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
}

const RequisitionDetailModal: React.FC<ComponentProps> = ({ visible, onHide }) => {
  const { t } = useTranslation('admin');
  const detail = useAdminRequisitionStore(s => s.detail);
  const detailLoading = useAdminRequisitionStore(s => s.detailLoading);
  const detailError = useAdminRequisitionStore(s => s.detailError);

  const requisition = detail?.requisition;
  const header = requisition ? t('requisitions.detail.title', { id: requisition.id }) : t('requisitions.title');

  const field = (label: string, value?: React.ReactNode) => (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 whitespace-pre-line">{value || '-'}</div>
    </div>
  );

  return (
    <Modal visible={visible} header={header} onHide={onHide} width="w-[960px]" closeOnBackdrop={true}>
      <div className="p-4 space-y-6">
        {detailLoading && <div className="text-center text-gray-500">{t('requisitions.loading')}</div>}
        {detailError && !detailLoading && <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800">{detailError}</div>}

        {detail && requisition && (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {field(t('requisitions.columns.status'), t(`requisitions.status.${requisition.status}`))}
              {field(t('requisitions.columns.organization'), requisition.organizationName)}
              {field(t('requisitions.columns.supplier'), requisition.supplierName)}
              {field(t('requisitions.columns.author'), requisition.userName)}
              {field(t('requisitions.columns.portal'), requisition.portalName)}
              {field(t('requisitions.columns.deliveryDate'), formatDate(requisition.deliveryDate))}
              {field(t('requisitions.detail.created'), formatDateTime(requisition.created))}
              {field(
                t('requisitions.detail.deliveryAddress'),
                [detail.deliveryAddressName, detail.deliveryAddressStreet, [detail.deliveryAddressZip, detail.deliveryAddressCity].filter(Boolean).join(' ')]
                  .filter(Boolean)
                  .join(', '),
              )}
              {field(t('requisitions.detail.contactPerson'), [detail.contactPersonName, detail.contactPersonPhone].filter(Boolean).join(', '))}
              {field(t('requisitions.detail.note'), detail.note)}
              {detail.processedAt &&
                field(t('requisitions.detail.processed'), `${detail.processedByName ?? ''} · ${formatDateTime(detail.processedAt)}`)}
              {requisition.orderId &&
                field(
                  t('requisitions.columns.order'),
                  <Link to="/admin/orders/$orderId" params={{ orderId: requisition.orderId }} className="text-blue-600 hover:underline">
                    {requisition.orderNumber}
                  </Link>,
                )}
              {detail.rejectedAt &&
                field(
                  t('requisitions.detail.rejected'),
                  [`${detail.rejectedByName ?? ''} · ${formatDateTime(detail.rejectedAt)}`, detail.rejectReason].filter(Boolean).join('\n'),
                )}
            </div>

            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-2">{t('requisitions.items.product')}</th>
                  <th className="px-4 py-2 text-right">{t('requisitions.items.quantity')}</th>
                  <th className="px-4 py-2 text-right">{t('requisitions.items.price')}</th>
                  <th className="px-4 py-2 text-right">{t('requisitions.items.orderPrice')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {detail.items.map(item => (
                  <tr key={item.id}>
                    <td className="px-4 py-2">
                      <div className="text-gray-900">{item.productName}</div>
                      <div className="text-xs text-gray-400">{item.productEcid}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{formatAmountPlain(item.quantity)}</td>
                    <td className="px-4 py-2 text-right">{formatMoney(item.price)}</td>
                    <td className="px-4 py-2 text-right">{item.orderPrice != null ? formatMoney(item.orderPrice) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RequisitionDetailModal;
